import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { EMPIRICAL_RULE_CALCULATOR_STRINGS } from "@/components/tools/empirical-rule-calculator";
import {
  Explanation,
  Formula,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import { BellCurve } from "@/components/tools/stats-descriptive-more/bell-curve";
import { t } from "@/lib/i18n/interpolate";
import type { NumberFormatter } from "@/lib/tool-kit/stats-descriptive/format";
import {
  closeToRule,
  type RuleRow,
} from "@/lib/tool-kit/stats-descriptive-more/empirical-rule";

type Strings = typeof EMPIRICAL_RULE_CALCULATOR_STRINGS;

export interface RuleView {
  strings: Strings;
  fmt: NumberFormatter;
  num: (value: number) => string;
}

export function percent(view: RuleView, share: number, digits = 2): string {
  return t(view.strings.percentTemplate, {
    value: view.fmt(share * 100, { maximumFractionDigits: digits }),
  });
}

function range(row: RuleRow, view: RuleView): string {
  return t(view.strings.rangeTemplate, {
    from: view.num(row.lower),
    to: view.num(row.upper),
  });
}

export function RuleCards({
  rows,
  view,
}: {
  rows: readonly RuleRow[];
  view: RuleView;
}) {
  const { strings } = view;
  const labels = [strings.cardOne, strings.cardTwo, strings.cardThree];
  return (
    <StatGrid>
      {rows.slice(0, 3).map((row, index) => (
        <StatCard
          emphasis
          key={row.k}
          label={labels[index]}
          value={range(row, view)}
        />
      ))}
    </StatGrid>
  );
}

export function ruleTable(rows: readonly RuleRow[], view: RuleView) {
  const { strings, fmt, num } = view;
  const hasN = rows.some((row) => row.expected !== null);
  const hasData = rows.some((row) => row.actualCount !== null);
  const head = [
    strings.colK,
    strings.colRange,
    strings.colRule,
    strings.colNormal,
    strings.colTail,
    strings.colChebyshev,
    ...(hasN ? [strings.colExpected] : []),
    ...(hasData ? [strings.colActual] : []),
  ];
  const body = rows.map((row) => [
    t(strings.kTemplate, { k: num(row.k) }),
    range(row, view),
    row.rulePercent === null
      ? strings.notAvailable
      : t(strings.percentTemplate, { value: num(row.rulePercent) }),
    percent(view, row.normal),
    percent(view, (1 - row.normal) / 2),
    percent(view, row.chebyshev),
    ...(hasN
      ? [
          row.expected === null
            ? strings.notAvailable
            : fmt(row.expected, { maximumFractionDigits: 1 }),
        ]
      : []),
    ...(hasData
      ? [
          row.actualCount === null || row.actualShare === null
            ? strings.notAvailable
            : t(strings.actualTemplate, {
                count: fmt(row.actualCount),
                share: percent(view, row.actualShare, 1),
              }),
        ]
      : []),
  ]);
  return { head, body };
}

export function RuleCurve({
  mean,
  sd,
  view,
}: {
  mean: number;
  sd: number;
  view: RuleView;
}) {
  const { strings, num } = view;
  const ticks = [-3, -2, -1, 0, 1, 2, 3];
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.curveTitle}</h3>
      <BellCurve
        label={t(strings.curveLabel, {
          mean: num(mean),
          sd: num(sd),
          one: `${num(mean - sd)}–${num(mean + sd)}`,
          two: `${num(mean - 2 * sd)}–${num(mean + 2 * sd)}`,
          three: `${num(mean - 3 * sd)}–${num(mean + 3 * sd)}`,
        })}
        rows={[
          {
            label: strings.axisSd,
            ticks: ticks.map((z) => ({ z, text: num(z) })),
          },
          {
            label: strings.axisValue,
            ticks: ticks.map((z) => ({ z, text: num(mean + z * sd) })),
          },
        ]}
        shades={[
          { from: -3, to: 3, opacity: 0.14 },
          { from: -2, to: 2, opacity: 0.22 },
          { from: -1, to: 1, opacity: 0.34 },
        ]}
      />
      <p className="text-muted-foreground text-xs">{strings.curveNote}</p>
    </div>
  );
}

export function RuleWorking({
  rows,
  mean,
  sd,
  fromData,
  view,
}: {
  rows: readonly RuleRow[];
  mean: number;
  sd: number;
  fromData: number | null;
  view: RuleView;
}) {
  const { strings, fmt, num } = view;
  return (
    <WorkingPanel title={strings.workingTitle}>
      {fromData === null ? null : (
        <Formula>
          {t(strings.workData, {
            n: fmt(fromData),
            mean: num(mean),
            sd: num(sd),
          })}
        </Formula>
      )}
      {rows.map((row) => (
        <Formula key={row.k}>
          {t(strings.workInterval, {
            k: num(row.k),
            mean: num(mean),
            sd: num(sd),
            lower: num(row.lower),
            upper: num(row.upper),
          })}
        </Formula>
      ))}
      {rows.map((row) => (
        <Formula key={`c${row.k}`}>
          {t(row.k > 1 ? strings.workChebyshev : strings.workChebyshevOne, {
            k: num(row.k),
            value: percent(view, row.chebyshev),
          })}
        </Formula>
      ))}
      <Explanation>{strings.workNote}</Explanation>
    </WorkingPanel>
  );
}

/** Compares the share of pasted data within 1 and 2 SD with 68% and 95%. */
export function DataVerdict({
  rows,
  view,
}: {
  rows: readonly RuleRow[];
  view: RuleView;
}) {
  const one = rows[0]?.actualShare;
  const two = rows[1]?.actualShare;
  if (one == null || two == null) {
    return null;
  }
  const { strings } = view;
  return (
    <Explanation>
      {t(closeToRule(one, two) ? strings.verdictClose : strings.verdictFar, {
        one: percent(view, one, 1),
        two: percent(view, two, 1),
      })}
    </Explanation>
  );
}
